import type { Step } from "@views/Upholstery/types"
import { applyPdmOverlaysToDatoSteps } from "./mergePdmWithDatoSteps"
import { extractConfigurableStepsFromPdmProduct } from "./extractConfigurableSteps"

/**
 * Upholstery always loads products via same-origin **`/api/pdm-products`**
 * (server **`PDM_AUTOMOTIVE_API_TOKEN`**). That route caches successful PDM
 * responses for ~1 hour so visitors share one upstream call.
 *
 * Query params (brand codes, limit, page, staging) still come from
 * **`NEXT_PUBLIC_PDM_*`** env vars so the browser can shape the proxy URL.
 */

/** True when `NEXT_PUBLIC_PDM_USE_STAGING_DATABASE=true`. */
function isPdmStagingDatabaseEnabled(): boolean {
  return (
    typeof process.env.NEXT_PUBLIC_PDM_USE_STAGING_DATABASE === "string" &&
    process.env.NEXT_PUBLIC_PDM_USE_STAGING_DATABASE.trim().toLowerCase() ===
      "true"
  )
}

/**
 * Locate an array of product-like objects somewhere in the PDM list payload.
 */
function firstArrayOfRecords(
  payload: unknown
): Record<string, unknown>[] | null {
  if (!payload || typeof payload !== "object") {
    return null
  }

  if (Array.isArray(payload)) {
    return payload.filter(
      (x): x is Record<string, unknown> =>
        typeof x === "object" && x !== null && !Array.isArray(x)
    )
  }

  const o = payload as Record<string, unknown>
  const keys = ["data", "products", "results", "items", "records"]

  for (const k of keys) {
    const v = o[k]
    if (Array.isArray(v)) {
      return v.filter(
        (x): x is Record<string, unknown> =>
          typeof x === "object" && x !== null && !Array.isArray(x)
      )
    }
  }

  return null
}

const SKU_FIELDS = [
  "sku",
  "SKU",
  "part_number",
  "PartNumber",
  "partNumber",
  "base_part_number",
  "basePartNumber",
  "ManufacturerPartNumber",
  "mfr_part_number",
  "item_id",
  "itemId",
]

function skuOnProduct(p: Record<string, unknown>): string[] {
  const out: string[] = []
  for (const field of SKU_FIELDS) {
    const v = p[field]
    if (typeof v === "string" && v.trim() !== "") {
      out.push(v.trim())
    }
    if (typeof v === "number") {
      out.push(String(v).trim())
    }
  }
  return out
}

function productMatchesSku(
  p: Record<string, unknown>,
  vehicleSku: string
): boolean {
  const want = vehicleSku.trim()
  if (!want) return false
  return skuOnProduct(p).some((x) => x === want)
}

function pickProductForVehicle(
  rows: Record<string, unknown>[],
  vehicleSku: string
): Record<string, unknown> | null {
  const bySku = rows.find((p) => productMatchesSku(p, vehicleSku))
  if (bySku) return bySku
  /** Single-row convenience when PDM filtered list already narrowed to one product */
  if (rows.length === 1) return rows[0] ?? null
  return null
}

/** Same-origin proxy URL; browser never talks to api.pdm-automotive.com. */
function buildPdmProductsProxyRelativeUrl(): string {
  const qp = new URLSearchParams()

  const brand =
    typeof process.env.NEXT_PUBLIC_PDM_BRAND_CODES === "string"
      ? process.env.NEXT_PUBLIC_PDM_BRAND_CODES.trim()
      : ""
  if (brand !== "") qp.set("brand_codes", brand)

  const limit =
    typeof process.env.NEXT_PUBLIC_PDM_PRODUCTS_LIMIT === "string"
      ? process.env.NEXT_PUBLIC_PDM_PRODUCTS_LIMIT.trim()
      : ""
  if (limit !== "") qp.set("limit", limit)

  const page =
    typeof process.env.NEXT_PUBLIC_PDM_PRODUCTS_PAGE === "string"
      ? process.env.NEXT_PUBLIC_PDM_PRODUCTS_PAGE.trim()
      : ""
  if (page !== "") qp.set("page", page)

  if (isPdmStagingDatabaseEnabled()) {
    qp.set("use_staging_database", "1")
  }

  const qs = qp.toString()
  return qs.length > 0 ? `/api/pdm-products?${qs}` : `/api/pdm-products`
}

async function fetchPdmProductsPayload(): Promise<Response | null> {
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn(
      "[PDM Upholstery] requesting `/api/pdm-products` (server token + ~1h cache)"
    )
  }
  return fetch(buildPdmProductsProxyRelativeUrl(), { method: "GET" })
}

/**
 * Fetches PDM and overlays configurable steps.
 * Silent no-op when misconfigured / CORS / HTTP errors / no overlays.
 */
export async function fetchAndMergePdmConfigurableSteps(
  datoSteps: Step[],
  vehicleSku: string
): Promise<Step[]> {
  if (
    typeof window === "undefined" ||
    typeof vehicleSku !== "string" ||
    vehicleSku.trim() === ""
  ) {
    return datoSteps
  }

  try {
    const rows = await fetchPdmProductsRecords()
    if (!rows) {
      return datoSteps
    }

    const product = pickProductForVehicle(rows, vehicleSku)
    if (!product) {
      return datoSteps
    }

    const overlays = extractConfigurableStepsFromPdmProduct(product)

    /** Dev-only breadcrumb until PDM blob mapping is finalized */
    if (
      process.env.NODE_ENV === "development" &&
      overlays == null &&
      isPlainObject(product)
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        "[PDM Upholstery] Matched product for SKU",
        vehicleSku,
        "but no Upholstery overlay was found yet. Inspect PDM payload and extend extractConfigurableSteps.ts"
      )
    }

    if (!overlays) {
      return datoSteps
    }

    return applyPdmOverlaysToDatoSteps(datoSteps, overlays)
  } catch {
    return datoSteps
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v)
}

/** Successful responses are cached for the browser tab session. Failed/null responses are not cached. */
let pdmProductsCache: Record<string, unknown>[] | null | undefined = undefined

let pdmProductsInflight: Promise<Record<string, unknown>[] | null> | null = null

async function loadPdmProductsRecordsFromNetwork(): Promise<
  Record<string, unknown>[] | null
> {
  try {
    const res = await fetchPdmProductsPayload()
    if (!res || !res.ok) {
      console.error("❌ [PDM Fetch] Request failed:", res?.status)
      return null
    }
    const payload = (await res.json()) as unknown
    if (isPlainObject(payload) && typeof payload.error === "string") {
      console.error("❌ [PDM Fetch] API returned error:", payload.error)
      return null
    }
    const rows = firstArrayOfRecords(payload)
    return rows && rows.length > 0 ? rows : null
  } catch (err) {
    console.error("❌ [PDM Fetch] Exception:", err)
    return null
  }
}

/**
 * Loads `products[]` via `/api/pdm-products` (server caches ~1h upstream).
 * The first full response is also cached for the tab session so legacy merge + Upholstery V2 do
 * not refetch on every vehicle pick; concurrent callers share one HTTP request.
 * Call **`prefetchPdmProductsRecords()`** from the `/upholstery` page on mount to warm cache.
 */
export async function fetchPdmProductsRecords(): Promise<
  Record<string, unknown>[] | null
> {
  if (typeof window === "undefined") {
    return null
  }

  if (pdmProductsCache !== undefined) {
    return pdmProductsCache
  }

  if (!pdmProductsInflight) {
    pdmProductsInflight = loadPdmProductsRecordsFromNetwork()
      .then((rows) => {
        if (rows !== null) {
          pdmProductsCache = rows
        } else {
          console.warn(
            "[PDM Cache] Not caching failed PDM products response; retry allowed on next selection"
          )
        }
        return rows
      })
      .finally(() => {
        pdmProductsInflight = null
      })
  }

  return pdmProductsInflight
}

/** Start PDM fetch as soon as the upholstery experience loads (optional; merges use cache). */
export function prefetchPdmProductsRecords(): void {
  if (typeof window === "undefined") return
  void fetchPdmProductsRecords()
}
